import React, { useEffect, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Alert, AlertDescription } from '../../ui';
import { api } from '../../../services/api';
import { useActionToast } from '../../../services/actionToast';
import { DataSkeleton } from '../../states';
import { passwordConfirmationField, passwordField } from '../../../utils/passwordValidation';
import { FormButton } from '../../buttons/FormButton';
import { PasswordInputForm } from '../../inputs/forms/PasswordInputForm';

const schema = Yup.object().shape({
  new_password: passwordField,
  confirmation: passwordConfirmationField,
});

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);
  const toast = useActionToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: { new_password: '', confirmation: '' },
  });

  useEffect(() => {
    let active = true;
    const validateToken = async () => {
      if (!token) {
        setTokenState('invalid');
        return;
      }
      try {
        await api.post('/auth/validate-reset-token', { token });
        if (active) {
          setTokenState('valid');
        }
      } catch (error) {
        if (active) {
          setTokenState('invalid');
        }
      }
    };
    validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (values: any) => {
    setServerError('');
    try {
      await api.post('/auth/reset-password', { token, ...values });
      setSuccess(true);
      toast({
        status: 'success',
        title: 'Senha redefinida',
        description: 'Use a nova senha para entrar novamente.',
      });
    } catch {
      const feedback =
        'Não foi possível redefinir a senha. Solicite um novo link ou tente novamente.';
      setServerError(feedback);
      toast({ status: 'error', title: 'Senha não redefinida', description: feedback });
    }
  };

  const onInvalid = () => {
    toast({
      status: 'error',
      title: 'Revise o formulário',
      description: 'Corrija os campos indicados antes de enviar.',
      id: 'reset-password-form-invalid',
    });
  };

  if (tokenState === 'loading') {
    return <DataSkeleton variant="form" rows={2} label="Validando link de redefinição" />;
  }

  if (tokenState === 'invalid') {
    return (
      <Flex direction="column" gap="16px">
        <Alert variant="error" data-testid="invalid-reset-token">
          <AlertDescription>O link é inválido, expirou ou já foi usado.</AlertDescription>
        </Alert>
        <Button as={Link} to="/recover" width="100%">
          Solicitar outro link
        </Button>
      </Flex>
    );
  }

  if (success) {
    return (
      <Flex direction="column" gap="16px" data-testid="reset-success">
        <Alert variant="success">
          <AlertDescription>Senha redefinida. Entre novamente.</AlertDescription>
        </Alert>
        <Button as={Link} to="/login" width="100%">
          Ir para o login
        </Button>
      </Flex>
    );
  }

  return (
    <Flex
      as="form"
      direction="column"
      gap="16px"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      noValidate
    >
      <PasswordInputForm
        name="new_password"
        label="Nova senha"
        placeholder="Digite a nova senha"
        register={register}
        errors={errors}
        isRequired
        autoComplete="new-password"
      />
      <PasswordInputForm
        name="confirmation"
        label="Confirmar nova senha"
        placeholder="Digite a senha novamente"
        register={register}
        errors={errors}
        isRequired
        autoComplete="new-password"
      />
      {serverError && (
        <Alert variant="error">
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      <FormButton mt="8px" width="100%" type="submit" isLoading={isSubmitting}>
        Redefinir senha
      </FormButton>
    </Flex>
  );
}
